import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Alert,
  FormControlLabel,
  Switch,
  IconButton,
  Tooltip,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Paper,
  Tabs,
  Tab,
  InputAdornment,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { useTranslation } from 'react-i18next';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CloseIcon from '@mui/icons-material/Close';
import EventIcon from '@mui/icons-material/Event';
import axios from '../../utils/axios';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import SearchIcon from '@mui/icons-material/Search';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';

const UserManagement = () => {
  const { t } = useTranslation();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    role: 'user',
    maxActivities: 0,
    remainingActivities: 0,
    subscriptionStatus: 'inactive',
    subscriptionStartDate: null,
    subscriptionEndDate: null,
    location: '',
    bio: '',
    phoneNumber: '',
    newsletterSubscribed: false,
  });
  const [successMessage, setSuccessMessage] = useState('');
  const [deleteConfirmDialog, setDeleteConfirmDialog] = useState({
    open: false,
    userId: null
  });
  const [activitiesDialog, setActivitiesDialog] = useState({
    open: false,
    user: null,
    activities: []
  });
  const [userDetailsDialog, setUserDetailsDialog] = useState({
    open: false,
    user: null
  });
  const [selectedTab, setSelectedTab] = useState(0);
  const [userActivities, setUserActivities] = useState({
    recent: [],
    future: []
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [pageSize, setPageSize] = useState(10);
  const [registerDialog, setRegisterDialog] = useState({
    open: false,
    selectedUser: null,
    availableActivities: [],
    selectedActivity: '',
    numberOfParticipants: 1,
    selectedCategory: ''
  });

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/admin/users');
      const validUsers = Array.isArray(response.data) ? response.data : [];
      const usersWithIds = validUsers.map(user => {
        const userId = user._id || user.id;
        return {
          ...user,
          id: userId,
          _id: userId,
          name: user.name || '',
          email: user.email || '',
          role: user.role || 'user',
          subscriptionId: user.subscriptionId || null,
          maxActivities: user.maxActivities || 0,
          remainingActivities: user.remainingActivities || 0,
          subscriptionStatus: user.subscriptionStatus || 'inactive',
          subscriptionStartDate: user.subscriptionStartDate || null,
          subscriptionEndDate: user.subscriptionEndDate || null,
          avatar: user.avatar || '',
          bio: user.bio || '',
          location: user.location || '',
          newsletterSubscribed: user.newsletterSubscribed !== false,
          registeredActivities: user.registeredActivities || [],
          savedActivities: user.savedActivities || [],
          phoneNumber: user.phoneNumber || '',
        };
      });
      setUsers(usersWithIds);
    } catch (error) {
      setError(t('errorLoading'));
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (user = null) => {
    if (user) {
      setSelectedUser(user);
      setFormData({
        name: user.name || '',
        email: user.email || '',
        role: user.role || 'user',
        maxActivities: user.maxActivities || 0,
        remainingActivities: user.remainingActivities || 0,
        subscriptionStatus: user.subscriptionStatus || 'inactive',
        subscriptionStartDate: user.subscriptionStartDate || null,
        subscriptionEndDate: user.subscriptionEndDate || null,
        location: user.location || '',
        bio: user.bio || '',
        phoneNumber: user.phoneNumber || '',
        newsletterSubscribed: user.newsletterSubscribed !== false,
      });
    } else {
      setSelectedUser(null);
      setFormData({
        name: '',
        email: '',
        role: 'user',
        maxActivities: 0,
        remainingActivities: 0,
        subscriptionStatus: 'inactive',
        subscriptionStartDate: null,
        subscriptionEndDate: null,
        location: '',
        bio: '',
        phoneNumber: '',
        newsletterSubscribed: true,
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedUser(null);
    setFormData({
      name: '',
      email: '',
      role: 'user',
      maxActivities: 0,
      remainingActivities: 0,
      subscriptionStatus: 'inactive',
      subscriptionStartDate: null,
      subscriptionEndDate: null,
      location: '',
      bio: '',
      phoneNumber: '',
      newsletterSubscribed: false,
    });
  };

  const handleSubmit = async () => {
    try {
      // Generate password from name and current date
      const date = new Date();
      const name = formData.name.trim(); // Ensure name is a clean string
      const nameParts = name.split(' ').map(part => 
        part.charAt(0).toUpperCase() + part.slice(1).toLowerCase()
      );
      const compressedName = nameParts.join('');
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const generatedPassword = `${compressedName}${year}${month}!`;

      // Set subscription status based on maxActivities
      const subscriptionStatus = formData.maxActivities > 0 ? 'active' : 'inactive';

      const userData = {
        ...formData,
        name: name, // Ensure we're using the cleaned name
        subscriptionStatus,
        password: generatedPassword,
      };

      if (selectedUser) {
        await axios.put(`/api/admin/users/${selectedUser._id}`, userData);
      } else {
        const response = await axios.post('/api/admin/users', userData);
        // Send notification emails
        await axios.post('/api/admin/users/notify-creation', {
          userId: response.data.user._id,
          password: generatedPassword
        });
      }
      handleCloseDialog();
      fetchUsers();
    } catch (error) {
      console.error('Error saving user:', error);
      setError(t('errorSaving'));
    }
  };

  const handleDeleteClick = (user) => {
    setDeleteConfirmDialog({
      open: true,
      userId: user._id
    });
  };

  const handleDeleteUser = async () => {
    try {
      setLoading(true);
      const response = await axios.delete(`/api/users/${deleteConfirmDialog.userId}`);
      
      // Show success message with cancelled activities info
      const cancelledActivities = response.data.cancelledActivities;
      setSuccessMessage(
        `Utilisateur supprimé avec succès${
          cancelledActivities > 0 
            ? `${cancelledActivities} activity registration${cancelledActivities > 1 ? 's were' : ' was'} cancelled.`
            : ''
        }`
      );
      
      // Refresh user list
      fetchUsers();
    } catch (error) {
      setError(t('errorDeleting'));
    } finally {
      setLoading(false);
      setDeleteConfirmDialog({ open: false, userId: null });
    }
  };

  const fetchUserActivities = async (user) => {
    try {
      setLoading(true);
      const response = await axios.get(`/api/users/${user._id}/activities`);
      setActivitiesDialog({
        open: true,
        user,
        activities: response.data
      });
    } catch (error) {
      setError(t('errorFetchingUserActivities'));
    } finally {
      setLoading(false);
    }
  };

  const handleViewUser = async (user) => {
    setUserDetailsDialog({
      open: true,
      user
    });
    
    try {
      const response = await axios.get(`/api/users/${user._id}/activities`);
      const now = new Date();
      const activities = response.data;
      
      setUserActivities({
        recent: activities.filter(activity => new Date(activity.date) <= now),
        future: activities.filter(activity => new Date(activity.date) > now)
      });
    } catch (error) {
      setError(t('errorFetchingUserActivities'));
    }
  };

  const handleTabChange = (event, newValue) => {
    setSelectedTab(newValue);
  };

  const getFilteredUsers = () => {
    if (!searchQuery) return users;
    
    const query = searchQuery.toLowerCase();
    return users.filter(user => 
      user.name?.toLowerCase().includes(query) ||
      user.email?.toLowerCase().includes(query) ||
      user.location?.toLowerCase().includes(query)
    );
  };

  const handleRegisterClick = async (user) => {
    try {
      const response = await axios.get('/api/activities');
      const activities = response.data.filter(activity => {
        const activityDate = new Date(activity.date);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        return activityDate >= today && activity.currentParticipants < activity.maxParticipants;
      });
      setRegisterDialog({
        open: true,
        selectedUser: user,
        availableActivities: activities,
        selectedActivity: '',
        numberOfParticipants: 1,
        selectedCategory: ''
      });
    } catch (error) {
      console.error('Error fetching activities:', error);
      setError('Error fetching available activities');
    }
  };

  const handleRegisterSubmit = async () => {
    try {
      const activity = registerDialog.availableActivities.find(a => a._id === registerDialog.selectedActivity);
      if (!activity) {
        setError('Please select an activity');
        return;
      }

      const response = await axios.post(`/api/activities/${activity._id}/register`, {
        numberOfParticipants: registerDialog.numberOfParticipants,
        useSubscription: true
      }, {
        headers: {
          'X-User-Id': registerDialog.selectedUser._id
        }
      });

      setSuccessMessage(t('registrationSuccessful'));
      setRegisterDialog({
        open: false,
        selectedUser: null,
        availableActivities: [],
        selectedActivity: '',
        numberOfParticipants: 1,
        selectedCategory: ''
      });
      fetchUsers();
    } catch (error) {
      console.error('Error registering user:', error);
      setError(error.response?.data?.message || 'Error registering user for activity');
    }
  };

  const columns = [
    {
      field: 'name',
      headerName: t('name'),
      flex: 1,
      minWidth: 150,
    },
    {
      field: 'email',
      headerName: t('email'),
      flex: 1,
      minWidth: 200,
    },
    {
      field: 'phoneNumber',
      headerName: t('phoneNumber'),
      flex: 1,
      minWidth: 150,
      valueGetter: (params) => params.row.phoneNumber || '-',
    },
    {
      field: 'role',
      headerName: t('role'),
      width: 120,
    },
    {
      field: 'subscriptionStatus',
      headerName: t('status'),
      width: 130,
      renderCell: (params) => (
        <Box
          sx={{
            px: { xs: 1, sm: 1.5 },
            py: { xs: 0.5, sm: 0.75 },
            borderRadius: 1,
            bgcolor: 
              params.value === 'active' ? 'success.lighter' :
              params.value === 'expired' ? 'error.lighter' : 'grey.100',
            color: 
              params.value === 'active' ? 'success.dark' :
              params.value === 'expired' ? 'error.dark' : 'text.secondary',
            fontSize: { xs: '0.75rem', sm: '0.875rem' }
          }}
        >
          {t(params.value)}
        </Box>
      ),
    },
    {
      field: 'actions',
      headerName: t('actions'),
      width: 180,
      sortable: false,
      renderCell: (params) => (
        <Stack direction="row" spacing={1}>
          <Tooltip title={t('view')}>
            <IconButton
              size="small"
              onClick={() => handleViewUser(params.row)}
              sx={{ 
                fontSize: { xs: '1.25rem', sm: '1.5rem' }
              }}
            >
              <VisibilityIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title={t('edit')}>
            <IconButton
              size="small"
              onClick={() => handleOpenDialog(params.row)}
              sx={{ 
                fontSize: { xs: '1.25rem', sm: '1.5rem' }
              }}
            >
              <EditIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title={t('register')}>
            <IconButton
              size="small"
              onClick={() => handleRegisterClick(params.row)}
              sx={{ 
                fontSize: { xs: '1.25rem', sm: '1.5rem' }
              }}
            >
              <EventIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title={t('delete')}>
            <IconButton
              size="small"
              onClick={() => handleDeleteClick(params.row)}
              sx={{ 
                fontSize: { xs: '1.25rem', sm: '1.5rem' }
              }}
            >
              <DeleteIcon />
            </IconButton>
          </Tooltip>
        </Stack>
      ),
    },
  ];

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" justifyContent="flex-end" alignItems="center" sx={{ mb: 3 }}>
        <Button
          variant="contained"
          onClick={() => handleOpenDialog()}
          sx={{ mb: 2 }}
        >
          {t('addUser')}
        </Button>
      </Stack>

      <TextField
        fullWidth
        variant="outlined"
        placeholder={t('search')}
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        sx={{ mb: 3 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          ),
        }}
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMessage('')}>
          {successMessage}
        </Alert>
      )}

      <Box sx={{ height: 600 }}>
        <DataGrid
          rows={getFilteredUsers()}
          loading={loading}
          columns={columns}
          pageSize={pageSize}
          onPageSizeChange={setPageSize}
          rowsPerPageOptions={[5, 10, 20]}
          disableSelectionOnClick
          sx={{
            '& .MuiDataGrid-columnHeader': {
              fontSize: { xs: '0.875rem', sm: '1rem' },
              fontWeight: 600
            },
            '& .MuiDataGrid-cell': {
              fontSize: { xs: '0.875rem', sm: '1rem' }
            }
          }}
        />
      </Box>

      <Dialog 
        open={openDialog} 
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle 
          sx={{ 
            pb: 1,
            fontSize: { xs: '1.25rem', sm: '1.5rem' },
            fontWeight: 600
          }}
        >
          {selectedUser ? t('editUser') : t('addUser')}
          <IconButton
            onClick={handleCloseDialog}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: 'grey.500'
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Stack spacing={3}>
              <Typography variant="h6" color="primary">
                {t('basicInformation')}
              </Typography>
              <TextField
                fullWidth
                label={t('name')}
                name="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                sx={{
                  '& .MuiInputLabel-root': {
                    fontSize: { xs: '0.9rem', sm: '1rem' }
                  },
                  '& .MuiInputBase-input': {
                    fontSize: { xs: '0.9rem', sm: '1rem' }
                  }
                }}
              />
              <TextField
                fullWidth
                label={t('email')}
                name="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                sx={{
                  '& .MuiInputLabel-root': {
                    fontSize: { xs: '0.9rem', sm: '1rem' }
                  },
                  '& .MuiInputBase-input': {
                    fontSize: { xs: '0.9rem', sm: '1rem' }
                  }
                }}
              />
              <TextField
                fullWidth
                label={t('phoneNumber')}
                name="phoneNumber"
                value={formData.phoneNumber}
                onChange={(e) => setFormData({ ...formData, phoneNumber: e.target.value })}
                placeholder="0612345678"
                helperText={t('phoneNumberFormat')}
                sx={{
                  '& .MuiInputLabel-root': {
                    fontSize: { xs: '0.9rem', sm: '1rem' }
                  },
                  '& .MuiInputBase-input': {
                    fontSize: { xs: '0.9rem', sm: '1rem' }
                  }
                }}
              />

              <Box sx={{ mt: 3 }}>
                <Stack direction="row" spacing={2}>
                  <TextField
                    fullWidth
                    label={t('remainingActivities')}
                    type="number"
                    value={formData.remainingActivities}
                    onChange={(e) => setFormData({ ...formData, remainingActivities: parseInt(e.target.value) || 0 })}
                    InputProps={{
                      inputProps: { min: 0 }
                    }}
                  />
                  <TextField
                    fullWidth
                    label={t('maxActivities')}
                    type="number"
                    value={formData.maxActivities}
                    onChange={(e) => setFormData({ ...formData, maxActivities: parseInt(e.target.value) || 0 })}
                    InputProps={{
                      inputProps: { min: 0 }
                    }}
                  />
                </Stack>
              </Box>

              <FormControl fullWidth>
                <InputLabel>{t('role')}</InputLabel>
                <Select
                  value={formData.role}
                  label={t('role')}
                  onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                >
                  <MenuItem value="user">{t('user')}</MenuItem>
                  <MenuItem value="admin">{t('admin')}</MenuItem>
                </Select>
              </FormControl>

              <FormControl fullWidth>
                <InputLabel>{t('subscriptionStatus')}</InputLabel>
                <Select
                  value={formData.subscriptionStatus}
                  label={t('subscriptionStatus')}
                  onChange={(e) => setFormData({ ...formData, subscriptionStatus: e.target.value })}
                >
                  <MenuItem value="active">{t('active')}</MenuItem>
                  <MenuItem value="inactive">{t('inactive')}</MenuItem>
                  <MenuItem value="expired">{t('expired')}</MenuItem>
                </Select>
              </FormControl>

              <TextField
                fullWidth
                label={t('location')}
                name="location"
                value={formData.location}
                onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                sx={{
                  '& .MuiInputLabel-root': {
                    fontSize: { xs: '0.9rem', sm: '1rem' }
                  },
                  '& .MuiInputBase-input': {
                    fontSize: { xs: '0.9rem', sm: '1rem' }
                  }
                }}
              />
              <TextField
                fullWidth
                label={t('bio')}
                name="bio"
                multiline
                rows={3}
                value={formData.bio}
                onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
                sx={{
                  '& .MuiInputLabel-root': {
                    fontSize: { xs: '0.9rem', sm: '1rem' }
                  },
                  '& .MuiInputBase-input': {
                    fontSize: { xs: '0.9rem', sm: '1rem' }
                  }
                }}
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.newsletterSubscribed}
                    onChange={(e) => setFormData({ ...formData, newsletterSubscribed: e.target.checked })}
                  />
                }
                label={t('newsletterSubscribed')}
              />
            </Stack>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button 
            onClick={handleCloseDialog}
            sx={{
              fontSize: { xs: '0.875rem', sm: '1rem' }
            }}
          >
            {t('cancel')}
          </Button>
          <Button 
            onClick={handleSubmit} 
            variant="contained"
            sx={{
              fontSize: { xs: '0.875rem', sm: '1rem' }
            }}
          >
            {selectedUser ? t('save') : t('add')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={deleteConfirmDialog.open}
        onClose={() => setDeleteConfirmDialog({ open: false, userId: null })}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle 
          sx={{ 
            fontSize: { xs: '1.25rem', sm: '1.5rem' },
            fontWeight: 600
          }}
        >
          {t('confirmDelete')}
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: { xs: '0.9rem', sm: '1rem' } }}>
            {t('deleteUserConfirmation')}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button 
            onClick={() => setDeleteConfirmDialog({ open: false, userId: null })}
            sx={{
              fontSize: { xs: '0.875rem', sm: '1rem' }
            }}
          >
            {t('cancel')}
          </Button>
          <Button 
            onClick={handleDeleteUser} 
            color="error" 
            variant="contained"
            sx={{
              fontSize: { xs: '0.875rem', sm: '1rem' }
            }}
          >
            {t('delete')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={activitiesDialog.open}
        onClose={() => setActivitiesDialog({ open: false, user: null, activities: [] })}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle 
          sx={{ 
            pb: 1,
            fontSize: { xs: '1.25rem', sm: '1.5rem' },
            fontWeight: 600
          }}
        >
          {t('userActivities')} - {activitiesDialog.user?.name}
          <IconButton
            onClick={() => setActivitiesDialog({ open: false, user: null, activities: [] })}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: 'grey.500'
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontSize: { xs: '0.875rem', sm: '1rem' }, fontWeight: 600 }}>
                    {t('activity')}
                  </TableCell>
                  <TableCell sx={{ fontSize: { xs: '0.875rem', sm: '1rem' }, fontWeight: 600 }}>
                    {t('date')}
                  </TableCell>
                  <TableCell sx={{ fontSize: { xs: '0.875rem', sm: '1rem' }, fontWeight: 600 }}>
                    {t('status')}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {activitiesDialog.activities.map((activity) => (
                  <TableRow key={activity._id}>
                    <TableCell sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                      {activity.title}
                    </TableCell>
                    <TableCell sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                      {format(new Date(activity.date), 'd MMMM HH:mm', { locale: fr })}
                    </TableCell>
                    <TableCell>
                      <Box
                        sx={{
                          px: { xs: 1, sm: 1.5 },
                          py: { xs: 0.5, sm: 0.75 },
                          borderRadius: 1,
                          bgcolor: 
                            activity.status === 'upcoming' ? 'primary.lighter' :
                            activity.status === 'completed' ? 'success.lighter' : 'grey.100',
                          color: 
                            activity.status === 'upcoming' ? 'primary.dark' :
                            activity.status === 'completed' ? 'success.dark' : 'text.secondary',
                          fontSize: { xs: '0.75rem', sm: '0.875rem' }
                        }}
                      >
                        {t(activity.status)}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
      </Dialog>

      <Dialog
        open={userDetailsDialog.open}
        onClose={() => {
          setUserDetailsDialog({ open: false, user: null });
          setSelectedTab(0);
          setUserActivities({ recent: [], future: [] });
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle 
          sx={{ 
            pb: 1,
            fontSize: { xs: '1.25rem', sm: '1.5rem' },
            fontWeight: 600
          }}
        >
          {t('userDetails')}
          <IconButton
            onClick={() => {
              setUserDetailsDialog({ open: false, user: null });
              setSelectedTab(0);
              setUserActivities({ recent: [], future: [] });
            }}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: 'grey.500'
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {userDetailsDialog.user && (
            <Box sx={{ width: '100%' }}>
              <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
                <Tabs value={selectedTab} onChange={handleTabChange}>
                  <Tab label={t('basicInfo')} />
                  <Tab label={t('activities')} />
                </Tabs>
              </Box>

              {selectedTab === 0 && (
                <Stack spacing={2}>
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      {t('name')}
                    </Typography>
                    <Typography variant="body1">
                      {userDetailsDialog.user.name}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      {t('email')}
                    </Typography>
                    <Typography variant="body1">
                      {userDetailsDialog.user.email}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      {t('phoneNumber')}
                    </Typography>
                    <Typography variant="body1">
                      {userDetailsDialog.user.phoneNumber || '-'}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      {t('role')}
                    </Typography>
                    <Typography variant="body1">
                      {userDetailsDialog.user.role}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      {t('location')}
                    </Typography>
                    <Typography variant="body1">
                      {userDetailsDialog.user.location || '-'}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      {t('bio')}
                    </Typography>
                    <Typography variant="body1">
                      {userDetailsDialog.user.bio || '-'}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      {t('subscriptionStatus')}
                    </Typography>
                    <Box
                      sx={{
                        display: 'inline-block',
                        px: 1,
                        py: 0.5,
                        borderRadius: 1,
                        bgcolor: 
                          userDetailsDialog.user.subscriptionStatus === 'active' ? 'success.lighter' :
                          userDetailsDialog.user.subscriptionStatus === 'expired' ? 'error.lighter' : 'grey.100',
                        color: 
                          userDetailsDialog.user.subscriptionStatus === 'active' ? 'success.dark' :
                          userDetailsDialog.user.subscriptionStatus === 'expired' ? 'error.dark' : 'text.secondary'
                      }}
                    >
                      {t(userDetailsDialog.user.subscriptionStatus)}
                    </Box>
                  </Box>
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      {t('activitiesRemaining')}
                    </Typography>
                    <Typography variant="body1">
                      {userDetailsDialog.user.remainingActivities}/{userDetailsDialog.user.maxActivities}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      {t('newsletter')}
                    </Typography>
                    <Typography variant="body1">
                      {userDetailsDialog.user.newsletterSubscribed ? t('newsletterSubscribed') : t('newsletterNotSubscribed')}
                    </Typography>
                  </Box>
                </Stack>
              )}

              {selectedTab === 1 && (
                <Stack spacing={3}>
                  <Box>
                    <Typography variant="h6" color="primary" gutterBottom>
                      {t('futureActivities')}
                    </Typography>
                    <TableContainer component={Paper} variant="outlined">
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell>{t('activity')}</TableCell>
                            <TableCell>{t('date')}</TableCell>
                            <TableCell>{t('status')}</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {userActivities.future.length > 0 ? (
                            userActivities.future.map((activity) => (
                              <TableRow key={activity._id}>
                                <TableCell>{activity.title}</TableCell>
                                <TableCell>{format(new Date(activity.date), 'd MMMM HH:mm', { locale: fr })}</TableCell>
                                <TableCell>
                                  <Box
                                    sx={{
                                      px: 1,
                                      py: 0.5,
                                      borderRadius: 1,
                                      display: 'inline-block',
                                      bgcolor: 'primary.lighter',
                                      color: 'primary.dark'
                                    }}
                                  >
                                    {t('upcoming')}
                                  </Box>
                                </TableCell>
                              </TableRow>
                            ))
                          ) : (
                            <TableRow>
                              <TableCell colSpan={3} align="center">
                                {t('noFutureActivities')}
                              </TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>

                  <Box>
                    <Typography variant="h6" color="primary" gutterBottom>
                      {t('recentActivities')}
                    </Typography>
                    <TableContainer component={Paper} variant="outlined">
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell>{t('activity')}</TableCell>
                            <TableCell>{t('date')}</TableCell>
                            <TableCell>{t('status')}</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {userActivities.recent.length > 0 ? (
                            userActivities.recent.map((activity) => (
                              <TableRow key={activity._id}>
                                <TableCell>{activity.title}</TableCell>
                                <TableCell>{format(new Date(activity.date), 'd MMMM HH:mm', { locale: fr })}</TableCell>
                                <TableCell>
                                  <Box
                                    sx={{
                                      px: 1,
                                      py: 0.5,
                                      borderRadius: 1,
                                      display: 'inline-block',
                                      bgcolor: 'success.lighter',
                                      color: 'success.dark'
                                    }}
                                  >
                                    {t('completed')}
                                  </Box>
                                </TableCell>
                              </TableRow>
                            ))
                          ) : (
                            <TableRow>
                              <TableCell colSpan={3} align="center">
                                {t('noRecentActivities')}
                              </TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                </Stack>
              )}
            </Box>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={registerDialog.open} onClose={() => setRegisterDialog({ ...registerDialog, open: false })}>
        <DialogTitle>{t('registerUserForActivity')}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <FormControl fullWidth>
              <InputLabel>{t('category')}</InputLabel>
              <Select
                value={registerDialog.selectedCategory}
                onChange={(e) => setRegisterDialog({ ...registerDialog, selectedCategory: e.target.value })}
                label={t('category')}
              >
                <MenuItem value="">{t('all')}</MenuItem>
                {Array.from(new Set(registerDialog.availableActivities.map(a => a.category))).map(category => (
                  <MenuItem key={category} value={category}>{category}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>{t('activity')}</InputLabel>
              <Select
                value={registerDialog.selectedActivity}
                onChange={(e) => setRegisterDialog({ ...registerDialog, selectedActivity: e.target.value })}
                label={t('activity')}
              >
                {registerDialog.availableActivities
                  .filter(activity => !registerDialog.selectedCategory || activity.category === registerDialog.selectedCategory)
                  .map(activity => (
                    <MenuItem key={activity._id} value={activity._id}>
                      {activity.title} - {format(new Date(activity.date), 'd MMMM HH:mm', { locale: fr })} ({activity.category})
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>

            <TextField
              fullWidth
              type="number"
              label={t('numberOfParticipants')}
              value={registerDialog.numberOfParticipants}
              onChange={(e) => setRegisterDialog({ ...registerDialog, numberOfParticipants: parseInt(e.target.value) })}
              inputProps={{ min: 1, max: 4 }}
              helperText={t('numberOfParticipantsHelper')}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRegisterDialog({ ...registerDialog, open: false })}>{t('cancel')}</Button>
          <Button onClick={handleRegisterSubmit} variant="contained" color="primary">
            {t('register')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UserManagement; 